// Generated by GitHub Copilot
/**
 * Feature Toggle Definitions API Service
 * Fetches feature toggle definitions from the backend API
 */
import { authFetch } from "../utils/authFetch";

/**
 * Fetch all feature toggle definitions from the API
 * @returns {Promise<Array>} Array of feature toggle definitions
 */
export const fetchFeatureToggleDefinitions = async () => {
  try {
    const response = await authFetch("/api/v1/config/feature-definitions", {
      method: "GET",
      credentials: "include", // Include cookies for authentication
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      // Handle different error status codes
      if (response.status === 401) {
        throw new Error(
          "Authentication required to access feature definitions"
        );
      } else if (response.status === 403) {
        throw new Error("Insufficient privileges to view feature definitions");
      } else if (response.status === 503) {
        throw new Error("Feature definitions service is currently unavailable");
      } else {
        throw new Error(
          `Failed to fetch feature definitions: ${response.status} ${response.statusText}`
        );
      }
    }

    const data = await response.json();

    // Validate response structure
    if (!data || !Array.isArray(data.feature_definitions)) {
      throw new Error("Invalid response format from feature definitions API");
    }

    return data.feature_definitions.map((definition) => ({
      id: definition.id,
      name: definition.name,
      displayName: definition.display_name,
      description: definition.description,
      category: definition.category,
      enabled: definition.enabled,
      experimental: definition.experimental,
      beta: definition.beta,
      dependencies: definition.dependencies || [],
      createdAt: definition.created_at,
      updatedAt: definition.updated_at,
    }));
  } catch (error) {
    console.error("Error fetching feature toggle definitions:", error);

    // For development/fallback, provide basic feature set
    // This ensures the UI doesn't break if the API is unavailable
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "Falling back to hardcoded feature definitions for development"
      );
      return [
        {
          id: "fallback-1",
          name: "onepagesummary",
          displayName: "One Page Summary",
          description:
            "Generate condensed one-page summaries of problem reports for quick review and distribution.",
          category: "stable",
          enabled: true,
          experimental: false,
          beta: false,
          dependencies: [],
        },
        {
          id: "fallback-2",
          name: "autogenerate",
          displayName: "Auto Generate",
          description:
            "Automatically generate content and suggestions based on problem data and historical patterns.",
          category: "stable",
          enabled: true,
          experimental: false,
          beta: false,
          dependencies: [],
        },
      ];
    }

    throw error;
  }
};

/**
 * Cache for feature toggle definitions to avoid excessive API calls
 */
let definitionsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get feature toggle definitions with caching
 * @param {boolean} forceRefresh - Force refresh the cache
 * @returns {Promise<Array>} Array of feature toggle definitions
 */
export const getFeatureToggleDefinitions = async (forceRefresh = false) => {
  const now = Date.now();

  // Return cached data if available and not expired
  if (
    !forceRefresh &&
    definitionsCache &&
    cacheTimestamp &&
    now - cacheTimestamp < CACHE_DURATION_MS
  ) {
    return definitionsCache;
  }

  try {
    const definitions = await fetchFeatureToggleDefinitions();

    // Update cache
    definitionsCache = definitions;
    cacheTimestamp = now;

    return definitions;
  } catch (error) {
    // If we have cached data and the API fails, return cached data with a warning
    if (definitionsCache) {
      console.warn("API failed, returning cached feature definitions");
      return definitionsCache;
    }

    throw error;
  }
};

/**
 * Clear the feature definitions cache
 */
export const clearFeatureDefinitionsCache = () => {
  definitionsCache = null;
  cacheTimestamp = null;
};
